import React from "react";

interface TargetUsersProps {
  isVisible: boolean;
}

const TargetUsers: React.FC<TargetUsersProps> = ({ isVisible }) => {
  const users = [
    {
      title: "Startups & Indie Hackers",
      description: "Building AI-powered products on a tight budget? Monitor OpenAI costs in real-time and prevent unexpected bills from derailing your runway.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      stats: "Save up to 40% on AI costs",
    },
    {
      title: "SaaS Development Teams",
      description: "Scale your AI features confidently with centralized budget management. Track usage across multiple projects, environments, and API keys in one dashboard.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      stats: "10+ hours saved per week",
    },
    {
      title: "Non-Technical Founders",
      description: "No coding required. Get a simple, visual dashboard that shows your AI API spending in plain language. Set budgets and receive alerts you can actually understand.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      stats: "5-minute setup",
    },
    {
      title: "Enterprise AI Teams",
      description: "Manage complex AI infrastructure with advanced features: role-based access, audit logs, custom integrations, and dedicated support for mission-critical applications.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      stats: "SOC 2 compliant",
    },
  ];

  return (
    <section id="target-users" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className={`max-w-6xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Who Uses Alertaio?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From solo developers to enterprise teams, Alertaio helps everyone take control of AI API costs.
            </p>
          </div>

          {/* User Types Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {users.map((user, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-8 hover:shadow-xl hover:border-indigo-300 transition-all duration-300"
              >
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-600 to-blue-600 text-white rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {user.icon}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {user.title}
                </h3>

                {/* Description */}
                <p className="text-slate-600 leading-relaxed mb-4">
                  {user.description}
                </p>

                {/* Stats Badge */}
                <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-sm font-semibold px-4 py-2 rounded-full">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {user.stats}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TargetUsers;
